<template>
  <div class="meeting-announcement">
    <!-- 主公告：长条形封面为背景图，标题在封面下方 -->
    <div
      class="main-card"
      v-if="mainItem"
      @click="openItem(mainItem)"
    >
      <div
        class="main-cover"
        :class="{ 'is-default': !mainItem.cover }"
        :style="coverStyle(mainItem)"
      >
        <!-- 封面图以背景形式铺满（真实接入时 mainItem.cover 为接口返回的封面图 url） -->
        <span v-if="!mainItem.cover" class="cover-mark">主公告</span>
      </div>
      <div class="main-body">
        <h2 class="main-title">{{ mainItem.title }}</h2>
      </div>
    </div>

    <!-- 次级公告列表：正方形封面 + 标题 -->
    <ul class="list">
      <li
        class="card"
        v-for="item in otherItems"
        :key="item.id"
        @click="openItem(item)"
      >
        <h2 class="card-title">{{ item.title }}</h2>
        <div
          class="sec-cover"
          :class="{ 'is-default': !item.cover }"
          :style="coverStyle(item)"
        ></div>
      </li>
    </ul>

    <!-- 加载中 -->
    <div class="page-state is-loading" v-if="loading">
      <p>数据加载中</p>
    </div>
    <!-- 空数据 / 缺少访问码 / 加载失败 -->
    <div class="page-state is-empty" v-else-if="!hasData">
      <p>{{ emptyText }}</p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import "../../common/sass/mixin.scss";

  .meeting-announcement {
    width: 100%;
    min-height: 100vh;
    background-color: #f4efe5;
    box-sizing: border-box;
    padding: 14px 0 48px;
  }

  /* 主公告卡：封面背景 + 下方标题 */
  .main-card {
    margin: 0 14px;
    background-color: #fff;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 6px 16px rgba(93, 62, 31, 0.08);
    cursor: pointer;
  }
  .main-cover {
    width: 100%;
    height: 120px; /* 封面图高度，可按需调整 */
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    &.is-default {
      background: linear-gradient(160deg, #C91B1F 0%, #7C0E12 100%);
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  .cover-mark {
    display: inline-block;
    padding: 6px 12px;
    border: 1px solid rgba(255, 255, 255, 0.8);
    border-radius: 999px;
    color: #fff;
    font-size: 13px;
    letter-spacing: 2px;
  }
  .main-body {
    padding: 16px 18px 18px;
  }
  .main-title {
    margin: 0;
    font-family: "Microsoft YaHei", "PingFang SC", sans-serif;
    font-size: 17px;
    font-weight: bold;
    color: #2b2118;
    line-height: 1.5;
    letter-spacing: 0.5px;
    @include ellipsis(2);
  }

  /* 次级公告卡片 */
  .list {
    list-style: none;
    margin: 12px 0 0;
    padding: 0 14px;
  }
  .card {
    display: flex;
    align-items: center;
    background-color: #fff;
    border-radius: 12px;
    padding: 12px;
    margin-bottom: 12px;
    box-shadow: 0 6px 16px rgba(93, 62, 31, 0.06);
    cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s;
  }
  .card:active {
    transform: translateY(1px) scale(0.995);
    box-shadow: 0 2px 6px rgba(93, 62, 31, 0.08);
  }
  /* 次级公告正方形封面 */
  .sec-cover {
    flex-shrink: 0;
    width: 76px;
    height: 76px;
    border-radius: 8px;
    margin-left: 12px;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    &.is-default {
      background: linear-gradient(160deg, #C91B1F 0%, #7C0E12 100%);
    }
  }
  .card-title {
    flex: 1;
    min-width: 0;
    font-family: "Microsoft YaHei", "PingFang SC", sans-serif;
    font-size: 15px;
    font-weight: 500;
    color: #32291e;
    line-height: 1.55;
    margin: 0;
    @include ellipsis(2);
  }

  /* 加载中 / 空数据状态 */
  .page-state {
    padding: 80px 0 60px;
    text-align: center;
    background-repeat: no-repeat;
    background-position: center;
    p {
      margin: 0;
      font-family: "Microsoft YaHei", "PingFang SC", sans-serif;
      font-size: 15px;
      color: #9a8c78;
    }
    &.is-loading {
      background-image: url("../../assets/loading.gif");
      background-size: 40px 40px;
      p {
        margin-top: 70px;
      }
    }
    &.is-empty {
      background-image: url("../../assets/default.png");
      background-size: 100px 100px;
      p {
        margin-top: 120px;
      }
    }
  }
</style>

<script>
  import { getMeetingAnnouncementList } from '../../api/meetingAnnouncement.js'
  import { splitFiles, openFile } from '../../common/js/annex.js'
  import { saveAnnouncementList } from '../../common/js/cache.js'

  export default {
    name: 'MeetingAnnouncementList',
    data() {
      return {
        // 公告分组码：来自链接（?code=xxx 或 /home/MeetingAnnouncement/xxx）
        code: '',
        // 接口返回的公告列表：data[0] 为主公告(type=1)，其余为次级公告(type=2)
        list: [],
        loading: true,
        emptyText: '抱歉！暂无数据'
      }
    },
    computed: {
      // 主公告：type === 1，接口已保证它排在最前
      mainItem() {
        return this.list.find(item => Number(item.type) === 1) || null
      },
      // 次级公告：type === 2
      otherItems() {
        return this.list.filter(item => Number(item.type) === 2)
      },
      // 是否已取到数据（控制空态展示）
      hasData() {
        return this.list.length > 0
      }
    },
    created() {
      const raw = this.$route.query.code != null
        ? this.$route.query.code
        : this.$route.params.code
      this.code = raw == null ? '' : String(raw).trim()
      if (!this.code) {
        this.loading = false
        this.emptyText = '缺少访问码，请通过完整链接打开'
        return
      }
      this.getList()
    },
    methods: {
      // 按分组码拉取该组公告
      getList() {
        this.loading = true
        getMeetingAnnouncementList(this.code).then(res => {
          if (res.code === 200) {
            this.list = res.data || []
            // 缓存整组公告：详情页直接复用，不再请求接口
            saveAnnouncementList(this.code, this.list)
            this.emptyText = '抱歉！暂无数据'
          } else {
            this.list = []
            // 失败文案：新接口字段为 error，旧字段为 msg，两者都兼容
            const tip = (typeof res.error === 'string' && res.error) ||
              (typeof res.msg === 'string' && res.msg) || ''
            this.emptyText = tip || '公告加载失败'
          }
        }).catch(() => {
          this.list = []
          this.emptyText = '网络异常，请稍后重试'
        }).then(() => {
          this.loading = false
        })
      },
      // 封面作为背景图铺满；无 cover 时走占位渐变
      coverStyle(item) {
        return item && item.cover ? {backgroundImage: `url(${item.cover})`} : {}
      },
      // 点击公告：单个 pdf 直接打开；图片、批量附件进详情页查看
      openItem(item) {
        if (!item) return
        const { images, docs } = splitFiles(item.files)
        // 只有唯一一个非图片附件（pdf 等）：不进详情页，直接打开
        if (images.length === 0 && docs.length === 1) {
          openFile(docs[0].url)
          return
        }
        this.$router.push({
          path: `/home/MeetingAnnouncementInfo/${encodeURIComponent(this.code)}/${encodeURIComponent(item.id)}`
        })
      }
    }
  }
</script>


// WEBPACK FOOTER //
// src/components/announcement/MeetingAnnouncementList.vue